import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Car, MapPin, Phone, Mail, ArrowRight, Facebook, Instagram, ChevronDown, Twitter } from 'lucide-react';
import NewsletterBlock from './NewsletterBlock';
import AnimateIn from './AnimateIn';
import api from '../lib/api';

const footerLinks = {
  services: [
    { label: 'Vendre votre voiture', href: '/vendre-voiture-montelimar' },
    { label: 'Estimer votre véhicule', href: '/estimer-voiture' },
    { label: 'Acheter une voiture', href: '/acheter-voiture-montelimar' },
    { label: 'Dépôt-vente', href: '/depot-vente-montelimar' },
    { label: 'Entreprises / Flottes', href: '/entreprises' },
  ],
  resources: [
    { label: 'Blog', href: '/blog' },
    { label: 'FAQ', href: '/faq' },
    { label: 'À propos', href: '/a-propos' },
    { label: 'Contact', href: '/contact' },
  ],
  legal: [
    { label: 'Mentions légales', href: '/mentions-legales' },
    { label: 'Politique de confidentialité', href: '/politique-confidentialite' },
    { label: 'Avertissement éditorial', href: '/avertissement-editorial' },
  ]
};

export default function Footer() {
  const [communes, setCommunes] = useState([]);
  const [showAllZones, setShowAllZones] = useState(false);

  useEffect(() => {
    api.get('/communes').then(r => setCommunes(r.data || [])).catch(() => {});
  }, []);

  const displayedCommunes = showAllZones ? communes : communes.slice(0, 8);

  return (
    <footer data-testid="footer" className="relative bg-[#070707] border-t border-white/[0.04]">
      <div className="grain-overlay absolute inset-0 pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-24 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-14 lg:gap-8">
          {/* BRAND & CONTACT */}
          <AnimateIn direction="up" className="lg:col-span-1 min-w-0">
            <Link to="/" className="flex items-center gap-2.5 mb-5">
              <img src="/logo-guideautofrance-80.webp" alt="GuideAutoFrance" className="w-10 h-10 rounded-xl object-cover" width={40} height={40} />
              <span className="text-lg font-bold font-['Outfit']">
                Guide<span className="text-yellow-400">Auto</span>France
              </span>
            </Link>
            <p className="text-zinc-400 text-sm leading-relaxed mb-6">
              Agence de transactions automobiles à Montélimar. Franchise du réseau <a href="https://jevendsvotreauto.com/agence-de-montelimar/" target="_blank" rel="noopener noreferrer" className="text-yellow-400 hover:underline">jevendsvotreauto.com</a>. Vente, achat, estimation, dépôt-vente et carte grise.
            </p>
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-sm text-zinc-400">
                <MapPin className="w-4 h-4 text-yellow-400 flex-shrink-0" />
                <span>Rue Raymond Louis, 26200 Montélimar</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-zinc-400">
                <Phone className="w-4 h-4 text-yellow-400 flex-shrink-0" />
                <a href="tel:0475529820" className="hover:text-yellow-400 transition-colors">04 75 52 98 20</a>
              </div>
              <div className="flex items-center gap-3 text-sm text-zinc-400 min-w-0">
                <Mail className="w-4 h-4 text-yellow-400 flex-shrink-0" />
                <a href="mailto:montelimar@jevendsvotreauto.com" className="hover:text-yellow-400 transition-colors truncate text-xs">montelimar@jevendsvotreauto.com</a>
              </div>
            </div>
          </AnimateIn>

          {/* SERVICES */}
          <AnimateIn direction="up" delay={0.1}>
            <h4 className="font-['Outfit'] font-semibold text-white mb-5 text-xs uppercase tracking-[0.2em]">Services</h4>
            <ul className="space-y-3">
              {footerLinks.services.map(l => (
                <li key={l.href}>
                  <Link to={l.href} className="text-zinc-400 hover:text-yellow-400 text-sm transition-colors flex items-center gap-1.5 group">
                    <ArrowRight className="w-3 h-3 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all text-yellow-400" />
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
            <h4 className="font-['Outfit'] font-semibold text-white mb-4 text-xs uppercase tracking-[0.2em] mt-8">Légal</h4>
            <ul className="space-y-3">
              {footerLinks.legal.map(l => (
                <li key={l.href}>
                  <Link to={l.href} className="text-zinc-400 hover:text-yellow-400 text-sm transition-colors">{l.label}</Link>
                </li>
              ))}
            </ul>
          </AnimateIn>

          {/* RESSOURCES */}
          <AnimateIn direction="up" delay={0.15}>
            <h4 className="font-['Outfit'] font-semibold text-white mb-5 text-xs uppercase tracking-[0.2em]">Ressources</h4>
            <ul className="space-y-3">
              {footerLinks.resources.map(l => (
                <li key={l.href}>
                  <Link to={l.href} className="text-zinc-400 hover:text-yellow-400 text-sm transition-colors flex items-center gap-1.5 group">
                    <ArrowRight className="w-3 h-3 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all text-yellow-400" />
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </AnimateIn>

          {/* ZONES DESSERVIES */}
          <AnimateIn direction="up" delay={0.2}>
            <h4 className="font-['Outfit'] font-semibold text-white mb-5 text-xs uppercase tracking-[0.2em]">Zones desservies</h4>
            <ul className="space-y-2">
              {displayedCommunes.map(c => (
                <li key={c.slug}>
                  <Link to={`/zone/vendre-voiture-${c.slug}`} className="text-zinc-400 hover:text-yellow-400 text-xs transition-colors flex items-center gap-1.5 group">
                    <MapPin className="w-2.5 h-2.5 text-zinc-600 group-hover:text-yellow-400 flex-shrink-0" />
                    {c.name} <span className="text-zinc-600">({c.code_postal})</span>
                  </Link>
                </li>
              ))}
            </ul>
            {communes.length > 8 && (
              <button onClick={() => setShowAllZones(!showAllZones)}
                className="mt-3 text-yellow-400 text-xs hover:underline flex items-center gap-1 transition-colors">
                <ChevronDown className={`w-3 h-3 transition-transform ${showAllZones ? 'rotate-180' : ''}`} />
                {showAllZones ? 'Voir moins' : `Voir les ${communes.length - 8} autres zones`}
              </button>
            )}
          </AnimateIn>

          {/* NEWSLETTER */}
          <AnimateIn direction="up" delay={0.3}>
            <h4 className="font-['Outfit'] font-semibold text-white mb-5 text-xs uppercase tracking-[0.2em]">Newsletter</h4>
            <p className="text-zinc-400 text-sm mb-4">Recevez nos conseils auto et les dernières actualités.</p>
            <NewsletterBlock compact />
          </AnimateIn>
        </div>

        <div className="mt-20 pt-8 border-t border-white/[0.04]">
          <div className="flex items-center gap-3 mb-8 justify-center">
            {[
              { href: "https://www.facebook.com/people/Guide-Auto-France/61573327217714/", icon: Facebook, id: "social-facebook", label: "Facebook" },
              { href: "https://www.instagram.com/guideautofrance", icon: Instagram, id: "social-instagram", label: "Instagram" },
              { href: "https://x.com/guideautofrance", icon: Twitter, id: "social-twitter", label: "X (Twitter)" },
              { href: "https://fr.pinterest.com/guideautofrance/", icon: () => <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current"><path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 01.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z"/></svg>, id: "social-pinterest", label: "Pinterest" },
            ].map(s => (
              <a key={s.id} href={s.href} target="_blank" rel="dofollow noopener" data-testid={s.id}
                aria-label={s.label}
                className="w-10 h-10 glass-card rounded-full flex items-center justify-center hover:bg-yellow-400/10 hover:border-yellow-400/20 transition-all duration-300 group">
                <s.icon className="w-4 h-4 text-zinc-400 group-hover:text-yellow-400 transition-colors" />
              </a>
            ))}
          </div>
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-zinc-600 text-xs">&copy; {new Date().getFullYear()} GuideAutoFrance.fr — Tous droits réservés</p>
            <div className="flex items-center gap-4">
              <p className="text-zinc-600 text-xs">Montélimar, Drôme (26) — France</p>
              <Link to="/admin/login" data-testid="footer-admin-link" className="text-zinc-700 hover:text-zinc-400 text-xs transition-colors">Admin</Link>
            </div>
          </div>
          <p className="text-zinc-700 text-[10px] text-center mt-4">Franchise du réseau jevendsvotreauto.com — Entreprise indépendante</p>
        </div>
      </div>

      {/* GIANT BRAND TEXT */}
      <div className="relative" aria-hidden="true">
        <div className="flex justify-center select-none pt-4">
          <span className="font-['Outfit'] font-black text-[11vw] md:text-[9.5vw] leading-[0.9] tracking-[-0.03em] text-white/[0.08] whitespace-nowrap">
            GUIDE<span style={{ color: 'rgba(214,255,0,0.12)' }}>AUTO</span>FRANCE
          </span>
        </div>
        <div className="absolute inset-x-0 bottom-0 h-[45%] bg-gradient-to-t from-[#070707] to-transparent pointer-events-none" />
      </div>
    </footer>
  );
}
